import { css } from 'lit';

/*
 * Figma token map:
 *   Colors/Background/bg-primary         -> --asc-bg-primary
 *   Colors/Background/bg-disabled_subtle -> --asc-bg-disabled_subtle
 *   Colors/Border/border-primary         -> --asc-border-primary
 *   Colors/Border/border-brand           -> --asc-border-brand         (focused)
 *   Colors/Border/border-error           -> --asc-border-error         (destructive)
 *   Colors/Border/border-disabled        -> --asc-border-disabled
 *   Colors/Text/text-primary             -> --asc-text-primary
 *   Colors/Text/text-secondary           -> --asc-text-secondary
 *   Colors/Text/text-tertiary            -> --asc-text-tertiary
 *   Colors/Text/text-disabled            -> --asc-text-disabled
 *   Colors/Text/text-placeholder         -> --asc-text-placeholder
 *   Colors/Text/text-error-primary       -> --asc-text-error-primary
 *   Colors/Foreground/fg-tertiary        -> --asc-fg-tertiary          (affordance icons)
 *   Colors/Foreground/fg-disabled        -> --asc-fg-disabled          (disabled affordances)
 *   Colors/Foreground/fg-error-primary   -> --asc-fg-error-primary     (error icon)
 *   Colors/Border/border-tertiary        -> --asc-border-tertiary      (dividers)
 *   Effects/focus-ring                   -> --asc-focus-ring
 *   Effects/focus-ring-error             -> --asc-focus-ring-error
 *   radius-sm   (8px)  -> --asc-radius-sm  (field border-radius)
 *   radius-xxs  (2px)  -> --asc-radius-xxs (chip border-radius)
 *   spacing-none=0  xxs=2  xs=4  sm=6  md=8  lg=12  xl=16
 */

export const inputStyles = css`
  :host {
    display: block;
    font-family: var(--asc-font-family-body);
  }

  [hidden] {
    display: none !important;
  }

  /* ── Base ──────────────────────────────────────────────────────── */

  [part='base'] {
    display: flex;
    flex-direction: column;
    gap: var(--asc-input-field-gap, var(--asc-spacing-sm));
  }

  /* ── Label row ─────────────────────────────────────────────────── */

  [part='label-row'] {
    display: flex;
    align-items: center;
    font-size: var(--asc-input-label-font-size, var(--asc-font-size-text-sm));
    font-weight: var(--asc-font-weight-medium);
    line-height: var(--asc-line-height-text-sm);
    color: var(--asc-input-label-color, var(--asc-text-secondary));
    cursor: default;
  }

  /* ── Field row: field + trailing-button-row ────────────────────── */
  /* gap is managed in the trailing-button section below */

  [part='field-row'] {
    display: flex;
    align-items: stretch;
  }

  /* ── Field: visible input container ────────────────────────────── */
  /*
   * Figma: bg-primary, 1px border-primary, radius-sm (8px), shadow-xs drop shadow.
   * padding-x: 14px — no spacing token at exactly 14px (between spacing-lg=12 and spacing-xl=16).
   * The field grows vertically in tags mode when chips wrap to multiple rows.
   */

  [part='field'] {
    display: flex;
    flex: 1;
    align-items: center;
    min-width: 0;
    min-height: var(--asc-input-height-md, 44px); /* no-token: 44px standard md input height */
    padding-inline: var(--asc-input-padding-x, 14px); /* no-token: Figma 14px horizontal padding */
    padding-block: var(--asc-input-padding-y-md, var(--asc-spacing-md));
    box-sizing: border-box;
    background: var(--asc-input-bg, var(--asc-bg-primary));
    border: var(--asc-input-border-width, var(--asc-border-width)) solid
      var(--asc-input-border-color, var(--asc-border-primary));
    border-radius: var(--asc-input-border-radius, var(--asc-radius-sm));
    box-shadow: var(--asc-input-shadow, 0px 1px 2px 0px var(--asc-shadow-xs));
    overflow: hidden;
    cursor: text;
    transition:
      border-color var(--asc-transition-fast),
      box-shadow var(--asc-transition-fast);
  }

  /* sm size */
  :host([size='sm']) [part='field'] {
    min-height: var(--asc-input-height-sm, 36px); /* no-token: 36px standard sm input height */
    padding-block: var(--asc-input-padding-y-sm, var(--asc-spacing-xs));
  }

  /* Focus: :focus-within on the field container */
  [part='field']:focus-within {
    border-color: var(--asc-input-border-color-focus, var(--asc-border-brand));
    box-shadow:
      var(--asc-input-shadow, 0px 1px 2px 0px var(--asc-shadow-xs)),
      0 0 0 3px var(--asc-input-focus-ring, var(--asc-focus-ring));
    outline: none;
  }

  /* Destructive idle */
  :host([destructive]) [part='field'] {
    border-color: var(--asc-input-border-color-destructive, var(--asc-border-error));
  }

  /* Destructive focused */
  :host([destructive]) [part='field']:focus-within {
    border-color: var(--asc-input-border-color-destructive, var(--asc-border-error));
    box-shadow:
      0px 1px 2px 0px var(--asc-shadow-xs),
      0 0 0 3px var(--asc-focus-ring-error);
  }

  /* Disabled */
  :host([disabled]) [part='field'] {
    background: var(--asc-input-bg-disabled, var(--asc-bg-disabled_subtle));
    border-color: var(--asc-input-border-color-disabled, var(--asc-border-disabled));
    cursor: not-allowed;
    pointer-events: none;
  }

  /* ── Leading affordance ─────────────────────────────────────────── */

  [part='leading'] {
    display: flex;
    align-items: center;
    gap: var(--asc-input-affordance-gap, var(--asc-spacing-md));
    flex-shrink: 0;
    color: var(--asc-input-affordance-color, var(--asc-fg-tertiary));
    margin-inline-end: var(--asc-input-affordance-gap, var(--asc-spacing-md));
  }

  :host([disabled]) [part='leading'] {
    color: var(--asc-input-affordance-color-disabled, var(--asc-fg-disabled));
  }

  /* ── Trailing affordance ────────────────────────────────────────── */

  [part='trailing'] {
    display: flex;
    align-items: center;
    gap: var(--asc-input-affordance-gap, var(--asc-spacing-md));
    flex-shrink: 0;
    color: var(--asc-input-affordance-color, var(--asc-fg-tertiary));
    margin-inline-start: var(--asc-input-affordance-gap, var(--asc-spacing-md));
  }

  :host([disabled]) [part='trailing'] {
    color: var(--asc-input-affordance-color-disabled, var(--asc-fg-disabled));
  }

  /* Error icon color override */
  .error-icon {
    color: var(--asc-input-error-icon-color, var(--asc-fg-error-primary));
    display: flex;
    align-items: center;
  }

  /* ── Dividers ────────────────────────────────────────────────────── */

  [part='leading-divider'],
  [part='trailing-divider'] {
    display: block;
    width: 1px;
    align-self: stretch;
    background-color: var(--asc-input-divider-color, var(--asc-border-tertiary));
    flex-shrink: 0;
  }

  /* ── Dropdown affordance slots ───────────────────────────────────── */
  /*
   * Suppress the native browser focus ring on <select> elements slotted
   * as leading/trailing dropdowns. The field container's :focus-within rule
   * already provides the brand-colored focus indicator.
   */
  ::slotted(select) {
    outline: none;
  }

  /* ── Tag chips (tags mode) ────────────────────────────────────────── */
  /*
   * In tags mode the field uses a flex-start cross-axis so it can grow vertically.
   * Chips and the inline input are wrapped together in a .tags-area container that
   * flexes and wraps independently of leading/trailing affordances (which stay pinned).
   */
  :host([mode='tags']) [part='field'] {
    align-items: flex-start;
    /* Do NOT flex-wrap the field itself — leading/trailing must stay pinned */
    padding-block: var(--asc-input-padding-y-md, var(--asc-spacing-md));
  }

  :host([mode='tags'][size='sm']) [part='field'] {
    padding-block: var(--asc-input-padding-y-sm, var(--asc-spacing-xs));
  }

  /* Inner wrapper that holds chips + inline input together */
  .tags-area {
    display: flex;
    flex: 1;
    min-width: 0;
    flex-wrap: wrap;
    align-items: center;
    gap: var(--asc-input-chip-gap, var(--asc-spacing-md));
  }

  [part='chips'] {
    display: contents; /* chips render directly into the tags-area flex context */
    list-style: none;
    margin: 0;
    padding: 0;
  }

  /* Chip list item — minimal wrapper around <asc-badge> */
  [part='chip'] {
    display: inline-flex;
    align-items: center;
    list-style: none;
  }

  /* Remove button rendered inside the badge trailing slot */
  .tag-remove-btn {
    all: unset;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: inherit;
    border-radius: var(--asc-radius-full);
    line-height: 0;
  }

  .tag-remove-btn:hover {
    color: var(--asc-fg-primary);
  }

  .tag-remove-btn:focus-visible {
    outline: 2px solid var(--asc-focus-ring);
    outline-offset: 1px;
  }

  .tag-remove-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* ── Native input ───────────────────────────────────────────────── */

  [part='input'] {
    flex: 1;
    min-width: 80px;
    border: none;
    outline: none;
    background: transparent;
    font-family: var(--asc-input-font-family, var(--asc-font-family-body));
    font-size: var(--asc-input-font-size-md, var(--asc-font-size-text-md));
    font-weight: var(--asc-input-font-weight, var(--asc-font-weight-regular));
    line-height: var(--asc-line-height-text-md);
    color: var(--asc-input-text-color, var(--asc-text-primary));
    padding: 0;
    width: 100%;
    box-sizing: border-box;
    align-self: center;
  }

  :host([size='sm']) [part='input'] {
    font-size: var(--asc-input-font-size-sm, var(--asc-font-size-text-sm));
    line-height: var(--asc-line-height-text-sm);
  }

  [part='input']::placeholder {
    color: var(--asc-input-text-color-placeholder, var(--asc-text-placeholder));
  }

  [part='input']:disabled,
  :host([disabled]) [part='input'] {
    color: var(--asc-input-text-color-disabled, var(--asc-text-disabled));
    cursor: not-allowed;
  }

  /* tags mode input comes after chips in .tags-area flex context */
  :host([mode='tags']) [part='input'] {
    min-width: 60px;
    width: auto;
    flex: 1 1 60px;
    align-self: center;
  }

  /* ── Payment icon ────────────────────────────────────────────────── */

  .payment-icon {
    display: flex;
    align-items: center;
    color: var(--asc-input-affordance-color, var(--asc-fg-tertiary));
    flex-shrink: 0;
  }

  :host([disabled]) .payment-icon {
    color: var(--asc-input-affordance-color-disabled, var(--asc-fg-disabled));
  }

  /* ── Trailing action button (shadow DOM) ──────────────────────── */
  /*
   * The button lives in shadow DOM so we have full CSS control over all states.
   * The slot inside it accepts label content (text, icon SVG) from light DOM.
   * When a trailing-button slot is populated the field-row gets .has-trailing-button:
   *   - Field loses its right border and right radius
   *   - Button's left corners are square to create a flush visual join
   */

  [part='field-row'] {
    gap: var(--asc-input-attached-button-gap, var(--asc-spacing-xs));
  }

  [part='field-row'].has-trailing-button {
    gap: 0;
  }

  :host [part='field-row'].has-trailing-button [part='field'] {
    border-start-end-radius: 0;
    border-end-end-radius: 0;
    border-inline-end: none;
  }

  [part='trailing-button'] {
    appearance: none;
    -webkit-appearance: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: stretch;
    gap: var(--asc-spacing-xs);
    white-space: nowrap;
    box-sizing: border-box;
    padding-inline: var(--asc-spacing-xl);
    background: var(--asc-bg-primary);
    color: var(--asc-text-secondary);
    border: 1px solid var(--asc-border-primary);
    border-start-start-radius: 0;
    border-end-start-radius: 0;
    border-start-end-radius: var(--asc-radius-sm);
    border-end-end-radius: var(--asc-radius-sm);
    font-family: var(--asc-font-family-body);
    font-size: var(--asc-font-size-text-sm);
    font-weight: var(--asc-font-weight-semibold);
    line-height: 1;
    cursor: pointer;
    box-shadow: 0px 1px 2px 0px var(--asc-shadow-xs);
    transition:
      background-color var(--asc-transition-fast),
      color var(--asc-transition-fast);
  }

  [part='trailing-button']:hover {
    background: var(--asc-bg-secondary);
  }

  [part='trailing-button']:focus-visible {
    outline: 3px solid var(--asc-focus-ring);
    outline-offset: 0;
    position: relative;
    z-index: 1;
  }

  [part='trailing-button']:disabled,
  :host([disabled]) [part='trailing-button'] {
    color: var(--asc-text-disabled);
    background: var(--asc-bg-disabled_subtle);
    border-color: var(--asc-border-disabled);
    cursor: not-allowed;
    box-shadow: none;
  }

  /* ── Hint / error row ───────────────────────────────────────────── */

  [part='hint-row'] {
    display: flex;
    flex-direction: column;
  }

  ::slotted([slot='hint']),
  ::slotted([slot='error']) {
    font-family: var(--asc-font-family-body);
    font-size: var(--asc-input-hint-font-size, var(--asc-font-size-text-sm));
    font-weight: var(--asc-font-weight-regular);
    line-height: var(--asc-line-height-text-sm);
  }

  ::slotted([slot='hint']) {
    color: var(--asc-input-hint-color, var(--asc-text-tertiary));
  }

  ::slotted([slot='error']) {
    color: var(--asc-input-error-color, var(--asc-text-error-primary));
  }

  ::slotted([slot='label']) {
    font-size: var(--asc-input-label-font-size, var(--asc-font-size-text-sm));
    font-weight: var(--asc-font-weight-medium);
    line-height: var(--asc-line-height-text-sm);
    color: var(--asc-input-label-color, var(--asc-text-secondary));
  }
`;
